import { router, Stack } from 'expo-router';
import { useAtomValue } from 'jotai';
import { type DataTableRowEditCompleteEvent } from 'primereact/datatable';
import React, { useEffect } from 'react';
import { Spinner, useToastController, YStack } from 'smoothie';

import { FullPageContainer } from '../../common/components/GroveContainers';
import { GroveLogo } from '../../common/components/GroveLogo';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { adminUserAtom } from '../../entities/entityAtoms';
import { useGetAllAdminUsers } from '../hooks/useGetAllAdminUsers';
import { useUpdateAdminUserAndRefetch } from '../hooks/useUpdateAdminUserAndRefetch';
import type { UpdateAdminUserDto } from '../types/AdminUserTypes';
import { AdminUserTable } from './AdminUserTable';

export const AdminUserManagementPage = () => {
    const adminUser = useAtomValue(adminUserAtom);
    const canAccessAdminUsers = adminUser.authorization.adminUsers.canViewAll;
    const canEditAdminUsers = adminUser.authorization.adminUsers.canEditAll;

    const { data: adminUsers, isLoading } = useGetAllAdminUsers({});
    const { updateAdminUserAsync } = useUpdateAdminUserAndRefetch();

    const toast = useToastController();

    // Redirect if user doesn't have access
    useEffect(() => {
        if (!canAccessAdminUsers) {
            router.replace('/');
        }
    }, [canAccessAdminUsers]);

    const handleRowEditComplete = async ({
        newData,
        index,
    }: DataTableRowEditCompleteEvent) => {
        if (!canEditAdminUsers) return;

        const adminUserId = adminUsers?.[index]?.id;
        if (adminUserId == null) return;

        const admin: UpdateAdminUserDto = newData as UpdateAdminUserDto;

        // Convert the edited data to UpdateAdminUserDto format
        const updateData: UpdateAdminUserDto = {
            role: admin.role,
            guideId: admin.guideId,
        };

        try {
            await updateAdminUserAsync(adminUserId, updateData);
            toast.show('Updated admin user!', MutationSuccessToastOptions);
            if (adminUserId === adminUser.id) {
                window.location.reload();
            }
        } catch (err) {
            toast.show(
                'Error updating admin user',
                MutationErrorToastOptions(),
            );
        }
    };

    return (
        <>
            <Stack.Screen
                options={{
                    headerLeft: () => (
                        <YStack marginHorizontal={16}>
                            <GroveLogo />
                        </YStack>
                    ),
                    headerTitle: 'Admin User Management',
                    headerTitleAlign: 'left',
                    headerBackVisible: true,
                }}
            />
            <FullPageContainer marginVertical={24}>
                {isLoading ? (
                    <Spinner />
                ) : (
                    <AdminUserTable
                        adminUsers={adminUsers ?? []}
                        onRowEditComplete={handleRowEditComplete}
                    />
                )}
            </FullPageContainer>
        </>
    );
};
